<template>
  <ChannelHero v-model:show-member-list="showMemberList" />

  <LeaderboardModal v-model:is-open="showMemberList" />

  <div class="grid grid-cols-1 gap-y-7 mb-7" :class="$attrs.class">
    <div
      v-if="
        isGetMembershipButtonSkeletonVisible ||
        isGetChannelMembershipButtonVisible
      "
      class="mx-5"
    >
      <GetChannelMembershipButtonSkeleton
        v-if="isGetMembershipButtonSkeletonVisible"
      />
      <GetChannelMembershipButton
        v-else-if="isGetChannelMembershipButtonVisible"
      />
    </div>

    <ChannelRecentActivityRow
      v-if="isSectionVisible(ChannelSectionName.CommunityFeed)"
      class="px-5 h-fit"
    />

    <ChannelMvpLeaderboardSection
      v-if="isSectionVisible(ChannelSectionName.Leaderboard)"
    />

    <ContestsSection
      v-if="isSectionVisible(ChannelSectionName.Contests)"
      id="contests"
      :style="
        canUseTokenGating ? { 'grid-row': channelPositionI + 1 } : undefined
      "
      :contests="contests"
      :loading="loadingContests"
      :loading-more="loadingMoreContests"
      @refetch="refetchContests"
      @load-more="loadMoreContests"
    />

    <ShowSection
      v-if="isSectionVisible(ChannelSectionName.Shows)"
      id="shows"
      data-testid="channel-section-shows"
      :style="canUseTokenGating ? { 'grid-row': channelPositionI } : undefined"
      :shows="shows"
      :loading="loadingShows"
      :loading-more="loadingMoreShows"
      @load-more="loadMoreShows"
      @refetch="refetchShows"
    >
      <template #actions>
        <RouterLink
          :to="{
            name: RouteNames.ShowList,
            params: { channel: channelStore.channelSlug },
          }"
          custom
        >
          <template #default="{ navigate }">
            <ButtonV2 variant="subtle" size="md" class="px-6" @click="navigate">
              {{ $t("channel.buttons.browseAllShows") }}
            </ButtonV2>
          </template>
        </RouterLink>
      </template>
    </ShowSection>

    <OffersSection
      v-if="isSectionVisible(ChannelSectionName.Offers) && enableCouponOffers"
      :offers="offers"
      :loading="loadingOffers"
      :loading-more="loadingMoreOffers"
      @load-more="loadMoreOffers"
      @refetch="refetchOffers"
    />

    <ThreadsSection
      v-if="isSectionVisible(ChannelSectionName.Threads)"
      id="threads"
      class="px-5"
    />
  </div>

  <ReferralModal
    v-model:is-open="referralService.showReferralModal.value"
    :referral="referralService.currentReferralData.value"
    @invalid-id="onInvalidReferral"
    @close="clearReferralData"
    @completed-action="completedReferralAction"
  />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import { useInfiniteQuery } from "@/composables/use-infinite-query";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { useEmbedAuthenticationBroker } from "@/composables/auth/use-embed-authentication-broker";
import type { OfferCardData } from "./offers/offer-card.vue";
import type { OfferDetailModalData } from "./offers/offer-detail-modal.vue";

const ShowsForShowSectionDocument = gql(/* GraphQL */ `
  query ShowsForShowSection(
    $channel_id: String!
    $community_id: String
    $page: Float
    $limit: Float
  ) {
    showsForShowSection(
      channel_id: $channel_id
      community_id: $community_id
      page: $page
      limit: $limit
    ) {
      title
      description
      id
      scheduled_start_time
      status

      thumbnail

      channel_id
      channel {
        name
        id
        logo
      }
      created_by {
        id
        username
        avatar
      }
      show_translations {
        language
        title
        description
      }
      show_communities {
        community {
          id
        }
      }
    }
  }
`);

const ChannelOffersForSectionDocument = gql(/* GraphQL */ `
  query ChannelOffersForSection(
    $active_only: Boolean
    $page: Float
    $limit: Float
  ) {
    channelOffers(active_only: $active_only, page: $page, limit: $limit) {
      id
      ...OfferCardData
      ...OfferDetailModalData
    }
  }
`);
</script>

<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, ref } from "vue";
import type { ResultOf } from "@graphql-typed-document-node/core";
import {
  ContestsForChannelDocument,
  type ChannelSection,
  type Show,
  ChannelSectionName,
  ShowStatus,
  WebAppIntegrationMode,
} from "@/generated/graphql";
import { useChannelContestLiveUpdates } from "@/features/channel/services/use-channel-contest-live-updates";
import { useChannelVisitTracking } from "@/features/channel/services/use-channel-visit-tracking";

import { useChannelStore } from "@/stores/channel.store";
import { getDefaultDomainChannel } from "@/utils/channel";

import ChannelHero from "./channel-hero.vue";
import ShowSection from "./shows/show-section.vue";
import ThreadsSection from "@/components/channel/threads/threads-section.vue";
import ContestsSection from "@/components/channel/contests/contest-section.vue";
import OffersSection from "@/components/channel/offers/offers-section.vue";

import { useRoute, useRouter } from "vue-router";
import ButtonV2 from "../ui/button/button-v2.vue";

import { RouteNames } from "@/router/app.router";
import ChannelMvpLeaderboardSection from "../leaderboard/channel-mvp-leaderboard-section.vue";
import { useHead } from "@unhead/vue";
import { useReferralService } from "@/features/channel/services/use-referral-service";
import { useWaitForSelector } from "@/composables/use-wait-for-selector";
import { getUrlOrAbsoluteUrlFromOrigin } from "@/utils/misc";
import { useUserStore } from "@/stores/user.store";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import GetChannelMembershipButton from "./channel-page/get-channel-membership-button.vue";
import GetChannelMembershipButtonSkeleton from "@/components/ui/skeleton/get-channel-membership-button-skeleton.vue";
import ChannelRecentActivityRow from "./channel-recent-activity-row.vue";
import { useI18n } from "vue-i18n";
import { useAppStore } from "@/stores/app.store";
import { useQuery } from "@urql/vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";

// Lazy load
const ReferralModal = defineAsyncComponent(
  () => import("@/features/channel/referral/referral-modal.vue"),
);
const LeaderboardModal = defineAsyncComponent(
  () => import("@/components/leaderboard/leaderboard-modal.vue"),
);

const ChannelSectionsForChannelDocument = gql(/* GraphQL */ `
  query ChannelSections($channelId: String!) {
    channelSections(channel_id: $channelId) {
      id
      channel_id
      section_name
      is_visible
    }
  }
`);

const route = useRoute();
const { desktop } = useResponsiveHelpers();
const userStore = useUserStore();
const channelStore = useChannelStore();
const referralService = useReferralService();
const { t } = useI18n();
const { canUseTokenGating } = useChannelCapabilities();
const appStore = useAppStore();
const { getBoolean } = useFeatureFlags();
const enableCouponOffers = getBoolean("enable_coupon_offers");
useEmbedAuthenticationBroker();
useChannelVisitTracking(() => channelStore.channel?.id);

const { data: channelSections } = useQuery({
  query: ChannelSectionsForChannelDocument,
  variables: { channelId: channelStore.channelSlug! },
});

const DEFAULT_SECTIONS = [
  ChannelSectionName.CommunityFeed,
  ChannelSectionName.Leaderboard,
  ChannelSectionName.Offers,
];

const initialSectionsCount = computed(() => {
  const sections =
    channelSections.value?.channelSections ??
    channelStore.channelSections ??
    [];

  if (!sections.length) {
    return 0;
  }

  const visibleInitialSections = sections.filter(
    (section) =>
      (section.section_name === ChannelSectionName.Offers &&
        enableCouponOffers.value &&
        section.is_visible) ||
      (DEFAULT_SECTIONS.includes(section.section_name) && section.is_visible),
  ).length;

  return visibleInitialSections;
});

const channelPositionI = computed(() => {
  return Math.max(1, initialSectionsCount.value);
});

const showMemberList = ref(false);

const domain = window.location.host;
const defaultAppChannel = getDefaultDomainChannel(domain);
const pageTitle = computed(() =>
  channelStore.channelSlug === defaultAppChannel
    ? t("channel.titles.fanfest")
    : t("channel.titles.channelFanfest", {
        channelDescription: channelStore.channel?.description,
      }),
);

// Since the referral page redirects to the channel page immediately
// after fetching the referral metadata, we set the tags here to avoid
// any issues with OGs rendering.
useHead({
  title: () => pageTitle.value,
  meta: [
    {
      property: "og:title",
      content: computed(
        () =>
          referralService.currentReferralData.value?.ogTitle ?? pageTitle.value,
      ),
    },
    {
      property: "og:image",
      content: computed(() =>
        getUrlOrAbsoluteUrlFromOrigin(
          referralService.currentReferralData.value?.ogImageUrl ??
            channelStore.channel?.banner ??
            undefined,
        ),
      ),
    },
    {
      property: "og:description",
      content: computed(
        () => referralService.currentReferralData.value?.ogDescription ?? "",
      ),
    },
    {
      property: "og:type",
      content: computed(
        () =>
          referralService.currentReferralData.value?.ogType ??
          t("channel.meta.defaultOgType"),
      ),
    },
  ],
});

const {
  items: shows,
  loading: queryLoadingShows,
  loadMore: loadMoreShows,
  refetch: refetchShows,
  hasInitialLoad: hasInitialLoadShows,
} = useInfiniteQuery({
  query: ShowsForShowSectionDocument,
  variables: () => ({
    channel_id: channelStore.channelSlug!,
    community_id: channelStore.selectedCommunity ?? undefined,
  }),
  limit: 10,
  pause: computed(() => !channelStore.channelSlug),
  extractData: (result) => result?.showsForShowSection as Show[],
});

const loadingShows = computed(
  () =>
    (queryLoadingShows.value && !hasInitialLoadShows.value) ||
    !channelStore.channel,
);

const loadingMoreShows = computed(
  () => queryLoadingShows.value && hasInitialLoadShows.value,
);

const {
  items: contests,
  loading: queryLoadingContests,
  loadMore: loadMoreContests,
  refetch: refetchContests,
  hasInitialLoad: hasInitialLoadContests,
} = useInfiniteQuery({
  query: ContestsForChannelDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    community_id: channelStore.selectedCommunity ?? undefined,
  })),
  limit: 10,
  pause: computed(() => !channelStore.channelSlug),
  extractData: (result) =>
    result?.contestsForChannel as ResultOf<
      typeof ContestsForChannelDocument
    >["contestsForChannel"],
  // Contest pages can briefly overlap across separate refetch requests when
  // the underlying list changes between two paginated fetches — de-dup by id
  // so the keyed contest rail never receives the same contest twice.
  dedupeKey: (contest) => contest.id,
});

const loadingContests = computed(
  () =>
    (queryLoadingContests.value && !hasInitialLoadContests.value) ||
    !channelStore.channel,
);

const loadingMoreContests = computed(
  () => queryLoadingContests.value && hasInitialLoadContests.value,
);

// Subscribe to per-live-show quiz-list and raffle-list Ably channels so
// the contests section reflects host actions (activate/end quiz or raffle)
// without requiring a user-initiated page reload.
const liveShowIds = computed(() =>
  (shows.value ?? [])
    .filter((show) => show.status === ShowStatus.Live)
    .map((show) => show.id),
);

useChannelContestLiveUpdates({
  channelId: computed(() => channelStore.channel?.id),
  liveShowIds,
  refetchContests,
});

const {
  items: offers,
  loading: queryLoadingOffers,
  loadMore: loadMoreOffers,
  refetch: refetchOffers,
  hasInitialLoad: hasInitialLoadOffers,
} = useInfiniteQuery({
  query: ChannelOffersForSectionDocument,
  variables: () => ({ active_only: true }),
  limit: 10,
  pause: computed(() => !channelStore.channelSlug),
  extractData: (result) =>
    (result?.channelOffers ?? []) as (OfferCardData & OfferDetailModalData)[],
});

const loadingOffers = computed(
  () =>
    (queryLoadingOffers.value && !hasInitialLoadOffers.value) ||
    !channelStore.channel,
);

const loadingMoreOffers = computed(
  () => queryLoadingOffers.value && hasInitialLoadOffers.value,
);

useWaitForSelector(
  computed(
    () =>
      route.hash ||
      // History stack is not reactive, but since its route-dependent and we're
      // also watching the route's hash, this works
      (history.state.scrollToSelector ?? null) ||
      null,
  ),
  async (el) => {
    await nextTick();
    el.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "center",
    });
  },
);

const router = useRouter();

/**
When the referral code is valid (query-able), but the payload is invalid
i.e. no experience of the type and id exists, we should redirect the user
to the channel page.
 */
function onInvalidReferral() {
  referralService.onInvalidReferralPayload();
  router.replace({
    name: RouteNames.Channel,
    params: { channel: channelStore.channelSlug },
  });
}

function clearReferralData() {
  referralService.resetAndCloseReferralModal();
}

function completedReferralAction() {
  refetchContests();
  clearReferralData();
}

function isSectionVisible(sectionName: ChannelSectionName): boolean {
  return (
    channelSections.value?.channelSections?.find(
      (section: ChannelSection) => section.section_name === sectionName,
    )?.is_visible ?? true
  );
}

// The button is hidden in embed mode because most SDK integrations handle
// auth on the host side. Exception: Fanfest Auth integration mode lets
// users log in directly inside the embedded iframe, so the button needs
// to be visible in that case.
const isMembershipButtonVisibleForContext = computed(
  () =>
    !appStore.isEmbed ||
    appStore.integrationMode === WebAppIntegrationMode.FanfestAuth,
);

const isGetMembershipButtonSkeletonVisible = computed(
  () =>
    desktop.value &&
    !userStore.isAuthenticated &&
    (channelStore.loadingChannel || !channelStore.channel) &&
    isMembershipButtonVisibleForContext.value,
);

const isGetChannelMembershipButtonVisible = computed(
  () =>
    desktop.value &&
    !userStore.isAuthenticated &&
    isMembershipButtonVisibleForContext.value,
);
</script>

<style scoped>
.channel-hero {
  min-height: 40vh;
  /* background-image: url('/dev_background.png'); */
}
</style>
